import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, forkJoin, of, switchMap } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, takeUntil } from 'rxjs/operators';

import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { ComponentCardComponent } from '../../../shared/components/common/component-card/component-card.component';
import { ButtonComponent } from '../../../shared/components/ui/button/button.component';
import { ToastService } from '../../../shared/services/toast.service';

import { AuthService } from '../../auth-pages/services/auth.service';
import { ContactoApiService } from '../../contactos/services/contacto-api.service';
import { EmpresaApiService } from '../../empresas/services/empresa-api.service';
import { ContactCompanyApiService } from '../../empresas/services/contact-company-api.service';
import { ContactCompanyResponse } from '../../empresas/models/empresa.model';

import { TicketsApiService } from '../services/tickets-api.service';
import { TicketsConfigApiService } from '../services/tickets-config-api.service';
import {
  Ticket,
  TicketRequest,
  TicketSource,
  TicketUpdateRequest
} from '../models/tickets.model';
import {
  TicketCategory,
  TicketPipeline,
  TicketPipelineStage,
  TicketPriority
} from '../models/tickets-config.model';
import {
  getOrigenLabel,
  getPlaceholder,
  resolveConfigLabel,
  PRIORIDAD_LABELS,
  buildCategoryTree,
  categoryIndent,          
  CategoryTreeNode 
} from '../utils/ticket-format.util';
import { UserApiService } from '../../users/services/user-api.service';
import { CrmUserResponse } from '../../users/models/user.model';

interface ContactRow {
  id: number;
  fullname: string;
  lastname?: string;
  mail?: string;
  phone?: string;
  companies?: { id: number; commercialName: string; phone?: string; isPrimary?: boolean }[];
}

interface CompanyRow {
  id: number;
  commercialName: string;
  phone?: string;
  /** Contacto principal REAL (resuelto por `contact_company`). NO usar `company.contactId`: es legado y llega null. */
  primaryContactId?: number | null;
  primaryContactName?: string | null;
}

const SOURCES: TicketSource[] = [
  'MANUAL',
  'EMAIL',
  'CHAT',
  'PHONE',
  'WEB_FORM',
  'PORTAL',
  'SOCIAL',
  'API'
];

@Component({
  selector: 'app-tickets-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    PageBreadcrumbComponent,
    ComponentCardComponent,
    ButtonComponent
  ],
  templateUrl: './tickets-form.component.html'
})
export class TicketsFormComponent implements OnInit, OnDestroy {
  // Modo dual
  @Input() drawerMode = false;
  @Input() ticketId: number | null = null;
  @Input() prefilledContactId: number | null = null;
  @Input() prefilledContactName: string | null = null;
  @Input() prefilledCompanyId: number | null = null;
  @Input() prefilledCompanyName: string | null = null;
  @Input() prefilledDealId: number | null = null;
  @Input() prefilledLeadId: number | null = null;   // Inc 25: lead de origen
  @Input() prefilledPipelineId: number | null = null;

  @Output() saved = new EventEmitter<Ticket>();
  @Output() cancelled = new EventEmitter<void>();

  /**
   * Inc 25 — vínculos de origen del ticket YA guardado (modo edición). Los
   * `prefilled*` solo llegan al crear desde el menú de acciones rápidas; sin
   * esto, editar un ticket creado desde un lead/negocio no re-enviaba idLead /
   * idDeal y podía perder la trazabilidad con el lead.
   */
  private loadedLeadId: number | null = null;
  private loadedDealId: number | null = null;

  private fb = inject(FormBuilder);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private ticketsApi = inject(TicketsApiService);
  private configApi = inject(TicketsConfigApiService);
  private contactoApi = inject(ContactoApiService);
  private empresaApi = inject(EmpresaApiService);
  private contactCompanyApi = inject(ContactCompanyApiService);
  private authService = inject(AuthService);
  private toast = inject(ToastService);
  private userApi = inject(UserApiService);

  form!: FormGroup;
  isEditing = false;
  loading = false;
  saving = false;
  error: string | null = null;

  /** Duplicados abiertos detectados al crear (INC 17). Si no es null, se muestra el aviso. */
  duplicateWarning: Ticket[] | null = null;

  // Owner mostrado en read-only (id del usuario actual; en edit el creador del ticket)
  ownerDisplayName = 'Usuario actual';
  private creatorUserId: number | null = null;

  // Catálogos
  pipelines: TicketPipeline[] = [];
  allStages: TicketPipelineStage[] = [];
  stages: TicketPipelineStage[] = [];
  priorities: TicketPriority[] = [];
  categories: TicketCategory[] = [];
  users: CrmUserResponse[] = [];
  categoryTree: CategoryTreeNode[] = [];
  readonly sources = SOURCES;

  // Asociaciones (typeahead)
  selectedContact: ContactRow | null = null;
  contactSearchTerm = '';
  contactSearchResults: ContactRow[] = [];
  searchingContacts = false;
  showContactDropdown = false;
  private contactSearch$ = new Subject<string>();

  selectedCompany: CompanyRow | null = null;
  companySearchTerm = '';
  companySearchResults: CompanyRow[] = [];
  searchingCompanies = false;
  showCompanyDropdown = false;
  private companySearch$ = new Subject<string>();

  // INC 3: autocompletado Contacto↔Empresa vía API de asociación.
  // Flags de "flash" visual cuando el campo opuesto se autocompleta.
  contactAutoFilled = false;
  companyAutoFilled = false;
  // Modal de elección de empresa cuando un contacto tiene varias asociadas.
  showCompanyChoiceModal = false;
  contactCompaniesForModal: ContactCompanyResponse[] = [];

  private readonly destroy$ = new Subject<void>();

  private avatarColors = [
    'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
    'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
    'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300',
    'bg-pink-100 text-pink-700 dark:bg-pink-900 dark:text-pink-300',
    'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300',
    'bg-indigo-100 text-indigo-700 dark:bg-indigo-900 dark:text-indigo-300',
    'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
    'bg-teal-100 text-teal-700 dark:bg-teal-900 dark:text-teal-300'
  ];

  ngOnInit(): void {
    this.initForm();
    this.setupSearchStreams();
    this.setupPipelineWatcher();
    this.cargarCatalogos();

    const idFromRoute = this.route.snapshot.paramMap.get('id');
    const id = this.ticketId ?? (idFromRoute ? Number(idFromRoute) : null);

    if (id) {
      this.isEditing = true;
      this.ticketId = id;
      this.loadTicket(id);
    } else {
      this.applyPrefills();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ===== Métodos públicos para el drawer wrapper =====
  reset(): void {
    this.isEditing = false;
    this.ticketId = null;
    this.error = null;
    this.saving = false;
    this.duplicateWarning = null;
    this.loadedLeadId = null;
    this.loadedDealId = null;
    this.selectedContact = null;
    this.selectedCompany = null;
    this.contactSearchTerm = '';
    this.companySearchTerm = '';
    this.contactSearchResults = [];
    this.companySearchResults = [];
    this.showContactDropdown = false;
    this.showCompanyDropdown = false;
    this.contactAutoFilled = false;
    this.companyAutoFilled = false;
    this.showCompanyChoiceModal = false;
    this.contactCompaniesForModal = [];
    if (this.form) {
      this.form.reset({
        subject: '',
        description: '',
        idTicketPriority: null,
        idTicketCategory: null,
        idPipeline: null,
        idStage: null,
        source: 'MANUAL',
        intIdUserOwner: null
      });
      const stageCtrl = this.form.get('idStage');
      stageCtrl?.disable();
    }
    this.applyDefaultSelections();
    this.applyPrefills();
  }

  loadTicket(id: number): void {
    this.loading = true;
    this.ticketsApi
      .getTicketById(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (ticket) => {
          this.creatorUserId = ticket.intIdUserCreator;
          // Inc 25: conservar el lead/negocio de origen para re-enviarlos al editar.
          this.loadedLeadId = ticket.idLead;
          this.loadedDealId = ticket.idDeal;
          this.form.patchValue({
            subject: ticket.subject,
            description: ticket.description ?? '',
            idTicketPriority: ticket.idTicketPriority,
            idTicketCategory: ticket.idTicketCategory,
            idPipeline: ticket.idPipeline,
            source: ticket.source,
            intIdUserOwner: ticket.intIdUserOwner
          });
          // source es inmutable en edición
          this.form.get('source')?.disable();

          if (ticket.idPipeline != null) {
            this.refreshStagesForPipeline(ticket.idPipeline, ticket.idStage);
          }

          // INC 13: mostrar el nombre legible del contacto/empresa que ya
          // viene enriquecido en el TicketResponse (sin caer a "#id").
          if (ticket.idContact != null && ticket.contactName) {
            this.selectedContact = { id: ticket.idContact, fullname: ticket.contactName };
          }
          if (ticket.idCompany != null && ticket.companyName) {
            this.selectedCompany = { id: ticket.idCompany, commercialName: ticket.companyName };
          }
          // Enriquecer con mail/teléfono (o resolver los que no traen nombre) en 2do plano.
          this.resolveContactAndCompany(
            ticket.idContact,
            ticket.idCompany,
            ticket.contactName,
            ticket.companyName
          );

          this.loading = false;
        },
        error: (err) => {
          this.error = err.error?.message || 'Error al cargar el ticket';
          this.loading = false;
        }
      });
  }

  /**
   * Resuelve Contacto y Empresa por ID a su nombre legible (para traer mail/teléfono),
   * en paralelo. Si la API falla, usa como fallback el nombre enriquecido del
   * TicketResponse (`fallbackContactName`/`fallbackCompanyName`) en vez de "#id".
   */
  private resolveContactAndCompany(
    idContact: number | null,
    idCompany: number | null,
    fallbackContactName?: string | null,
    fallbackCompanyName?: string | null
  ): void {
    const contact$ = idContact != null
      ? this.contactoApi.getContactById(idContact).pipe(catchError(() => of(null)))
      : of(null);

    const company$ = idCompany != null
      ? this.empresaApi.getCompanyById(idCompany).pipe(catchError(() => of(null)))
      : of(null);

    forkJoin({ contact: contact$, company: company$ })
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ contact, company }) => {
        if (idContact != null) {
          this.selectedContact = contact
            ? { id: contact.id!, fullname: contact.fullname, lastname: contact.lastname, mail: contact.mail, phone: contact.phone }
            : { id: idContact, fullname: (fallbackContactName || '').trim() || `Contacto #${idContact}` };
        }
        if (idCompany != null) {
          this.selectedCompany = company
            ? { id: company.id, commercialName: company.commercialName, phone: company.phone }
            : { id: idCompany, commercialName: (fallbackCompanyName || '').trim() || `Empresa #${idCompany}` };
        }
      });
  }

  // ===== Inicialización =====
  private initForm(): void {
    this.form = this.fb.group({
      subject: ['', [Validators.required, Validators.maxLength(255)]],
      description: [''],
      idTicketPriority: [null],
      idTicketCategory: [null],
      idPipeline: [null],
      idStage: [{ value: null, disabled: true }],
      source: ['MANUAL'],
      intIdUserOwner: [null, Validators.required] 
    });
  }

  private cargarCatalogos(): void {
    const businessId = this.authService.getCurrentUser()?.businessId || 1;

    forkJoin({
      pipelines: this.configApi.getPipelines().pipe(catchError(() => of([] as TicketPipeline[]))),
      priorities: this.configApi
        .getPriorities()
        .pipe(catchError(() => of([] as TicketPriority[]))),
      categories: this.configApi.getCategories().pipe(catchError(() => of([] as TicketCategory[]))),
      users: this.userApi.listActiveByBusiness(businessId).pipe(catchError(() => of([] as CrmUserResponse[])))
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ pipelines, priorities, categories,users }) => {
        this.pipelines = pipelines.filter(p => p.isActive !== false);
        this.priorities = priorities.filter(p => p.isActive !== false);
        this.categories = categories.filter(c => c.isActive !== false);
        this.categoryTree = buildCategoryTree(this.categories); 
        this.users = users.filter(u => u.isActive !== false);

        // Auto-seleccionar defaults solo en creación (no sobrescribir valores cargados al editar)
        if (!this.isEditing) {
          this.applyDefaultSelections();
        }

        // Si ya hay un pipeline seleccionado (prefill o load), refrescar stages.
        const currentPipelineId = this.form.get('idPipeline')?.value;
        if (currentPipelineId) {
          this.refreshStagesForPipeline(currentPipelineId);
        }
      });
  }

  private applyDefaultSelections(): void {
    const pipelineCtrl = this.form.get('idPipeline');
    const priorityCtrl = this.form.get('idTicketPriority');
    const ownerCtrl = this.form.get('intIdUserOwner');

    if (pipelineCtrl && pipelineCtrl.value == null) {
      const defaultPipeline = this.pipelines.find(p => p.isDefault) ?? this.pipelines[0];
      if (defaultPipeline) pipelineCtrl.setValue(defaultPipeline.id);
    }

    if (priorityCtrl && priorityCtrl.value == null) {
      // TicketPriority no tiene isDefault: se asume MEDIUM (nivel 3) como default del backend
      const defaultPriority =
        this.priorities.find(p => p.name === 'MEDIUM') ??
        this.priorities.find(p => p.level === 3) ??
        this.priorities[0];
      if (defaultPriority) priorityCtrl.setValue(defaultPriority.id);
    }

    if (ownerCtrl && ownerCtrl.value == null) {
      const currentUserId = this.authService.getCurrentUser()?.id;
      if (currentUserId) ownerCtrl.setValue(currentUserId);
    }
  }

  private setupPipelineWatcher(): void {
    this.form
      .get('idPipeline')!
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((idPipeline: number | null) => {
        if (idPipeline) {
          this.refreshStagesForPipeline(idPipeline);
        } else {
          this.stages = [];
          const stageCtrl = this.form.get('idStage');
          stageCtrl?.setValue(null);
          stageCtrl?.disable();
        }
      });
  }

  private refreshStagesForPipeline(pipelineId: number, preserveStageId?: number | null): void {
    this.configApi
      .getPipelineStages(pipelineId)
      .pipe(
        catchError(() => of([] as TicketPipelineStage[])),
        takeUntil(this.destroy$)
      )
      .subscribe((stages) => {
        this.stages = stages.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
        const stageCtrl = this.form.get('idStage');
        if (this.stages.length) {
          stageCtrl?.enable();
        } else {
          stageCtrl?.disable();
        }
        if (preserveStageId !== undefined && preserveStageId !== null) {
          stageCtrl?.setValue(preserveStageId);
        } else if (!this.stages.find((s) => s.id === stageCtrl?.value)) {
          // INC 46: en creación, preseleccionar la etapa "Nuevo" (o la primera por
          // sortOrder). En edición se respeta la etapa existente vía preserveStageId.
          if (!this.isEditing) {
            const defaultStage = this.pickDefaultStage(this.stages);
            stageCtrl?.setValue(defaultStage?.id ?? null);
          } else {
            stageCtrl?.setValue(null);
          }
        }
      });
  }

  /** Etapa por defecto para un ticket nuevo: "Nuevo" por nombre, o la primera por sortOrder. */
  private pickDefaultStage(stages: TicketPipelineStage[]): TicketPipelineStage | undefined {
    if (!stages.length) return undefined;
    const byName = stages.find((s) => (s.name || '').trim().toLowerCase() === 'nuevo');
    if (byName) return byName;
    // `stages` ya viene ordenado por sortOrder ascendente.
    return stages[0];
  }

  private applyPrefills(): void {
    if (this.prefilledPipelineId != null) {
      this.form.patchValue({ idPipeline: this.prefilledPipelineId });
    }
    if (this.prefilledContactId != null) {
      const name = (this.prefilledContactName ?? '').trim();
      this.selectedContact = {
        id: this.prefilledContactId,
        fullname: name || `Contacto #${this.prefilledContactId}`
      };
    }
    if (this.prefilledCompanyId != null) {
      const name = (this.prefilledCompanyName ?? '').trim();
      this.selectedCompany = {
        id: this.prefilledCompanyId,
        commercialName: name || `Empresa #${this.prefilledCompanyId}`
      };
    }
  }

  // ===== Typeaheads =====
  private setupSearchStreams(): void {
    this.contactSearch$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((term) => {
          if (!term || term.length < 2) {
            this.searchingContacts = false;
            return of([] as ContactRow[]);
          }
          this.searchingContacts = true;
          const businessId = this.authService.getCurrentUser()?.businessId || 1;
          return this.contactoApi
            .searchContacts({ businessId, searchTerm: term, page: 0, size: 10 })
            .pipe(
              map((res) =>
                res.content.map(
                  (c: any): ContactRow => ({
                    id: c.id,
                    fullname: c.fullname,
                    lastname: c.lastname,
                    mail: c.mail,
                    phone: c.phone,
                    companies: c.companies ?? []  
                  })
                )
              ),
              catchError(() => of([] as ContactRow[]))
            );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((results) => {
        this.contactSearchResults = results;
        this.searchingContacts = false;
        this.showContactDropdown = results.length > 0;
      });

    this.companySearch$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((term) => {
          if (!term || term.length < 2) {
            this.searchingCompanies = false;
            return of([] as CompanyRow[]);
          }
          this.searchingCompanies = true;
          const businessId = this.authService.getCurrentUser()?.businessId || 1;
          return this.empresaApi
            .searchCompanies({ businessId, searchTerm: term, page: 0, size: 10 })
            .pipe(
              map((res) =>
                res.content.map(
                  (c: any): CompanyRow => ({
                    id: c.id,
                    commercialName: c.commercialName,
                    phone: c.phone,
                    primaryContactId: c.primaryContactId,
                    primaryContactName: c.primaryContactName
                  })
                )
              ),
              catchError(() => of([] as CompanyRow[]))
            );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((results) => {
        this.companySearchResults = results;
        this.searchingCompanies = false;
        this.showCompanyDropdown = results.length > 0;
      });
  }

  onContactSearchInput(term: string): void {
    this.contactSearchTerm = term;
    this.contactSearch$.next(term);
  }

  hideContactDropdownDelayed(): void {
    setTimeout(() => {
      this.showContactDropdown = false;
    }, 200);
  }

  selectContact(contact: ContactRow): void {
    this.selectedContact = contact;
    this.contactSearchTerm = '';
    this.contactSearchResults = [];
    this.showContactDropdown = false;

    // INC 3: al elegir contacto se RESETEA la empresa y se re-resuelve vía la
    // API de asociación (patrón de Leads). 1 asociación → autocompleta;
    // varias → abre modal de elección.
    this.selectedCompany = null;
    this.companySearchTerm = '';
    this.companySearchResults = [];
    this.companyAutoFilled = false;
    this.showCompanyChoiceModal = false;
    this.contactCompaniesForModal = [];

    const businessId = this.authService.getCurrentUser()?.businessId || 1;
    this.contactCompanyApi
      .getByContact(contact.id, businessId)
      .pipe(
        catchError(() => of([] as ContactCompanyResponse[])),
        takeUntil(this.destroy$)
      )
      .subscribe((assocs) => {
        if (assocs.length === 1) {
          this.setCompanyFromAssoc(assocs[0]);
          this.flashCompanyAutoFill();
        } else if (assocs.length > 1) {
          this.contactCompaniesForModal = assocs;
          this.showCompanyChoiceModal = true;
        }
      });
  }

  quitarContacto(): void {
    this.selectedContact = null;
    this.contactAutoFilled = false;
  }

  onCompanySearchInput(term: string): void {
    this.companySearchTerm = term;
    this.companySearch$.next(term);
  }

  hideCompanyDropdownDelayed(): void {
    setTimeout(() => {
      this.showCompanyDropdown = false;
    }, 200);
  }

  selectCompany(company: CompanyRow): void {
    this.selectedCompany = company;
    this.companySearchTerm = '';
    this.companySearchResults = [];
    this.showCompanyDropdown = false;

    // INC 3: al elegir empresa se RESETEA el contacto y se toma el primario
    // (o el primero) desde la API de asociación.
    this.selectedContact = null;
    this.contactSearchTerm = '';
    this.contactSearchResults = [];
    this.contactAutoFilled = false;

    const businessId = this.authService.getCurrentUser()?.businessId || 1;
    this.contactCompanyApi
      .getByCompany(company.id, businessId)
      .pipe(
        catchError(() => of([] as ContactCompanyResponse[])),
        takeUntil(this.destroy$)
      )
      .subscribe((assocs) => {
        const primary = assocs.find((a) => a.isPrimary) || assocs[0];
        if (primary?.contact) {
          this.selectedContact = {
            id: primary.contact.id,
            fullname: primary.contact.fullname,
            lastname: primary.contact.lastname,
            mail: primary.contact.mail,
            phone: primary.contact.phone
          };
          this.flashContactAutoFill();
        } else if (company.primaryContactId) {
          // Respaldo: la asociación no devolvió nada utilizable, pero el listado de
          // empresas sí trae el contacto principal ya resuelto por el backend.
          this.selectedContact = {
            id: company.primaryContactId,
            fullname: company.primaryContactName?.trim() || `Contacto #${company.primaryContactId}`
          };
          this.flashContactAutoFill();
        }
      });
  }

  quitarEmpresa(): void {
    this.selectedCompany = null;
    this.companyAutoFilled = false;
  }

  // ===== INC 3: helpers de autocompletado Contacto↔Empresa =====
  /** Asigna la empresa seleccionada a partir de una asociación de la API. */
  private setCompanyFromAssoc(assoc: ContactCompanyResponse): void {
    this.selectedCompany = assoc.company
      ? { id: assoc.company.id, commercialName: assoc.company.commercialName, phone: assoc.company.phone }
      : { id: assoc.idCompany, commercialName: `Empresa #${assoc.idCompany}` };
  }

  /** El usuario elige una de las empresas asociadas al contacto (modal). */
  selectCompanyInModal(assoc: ContactCompanyResponse): void {
    this.setCompanyFromAssoc(assoc);
    this.showCompanyChoiceModal = false;
    this.contactCompaniesForModal = [];
    this.flashCompanyAutoFill();
  }

  /** Cierra el modal sin elegir empresa (el usuario la buscará manualmente). */
  skipCompanyChoice(): void {
    this.showCompanyChoiceModal = false;
    this.contactCompaniesForModal = [];
  }

  private flashCompanyAutoFill(): void {
    this.companyAutoFilled = true;
    setTimeout(() => (this.companyAutoFilled = false), 2500);
  }

  private flashContactAutoFill(): void {
    this.contactAutoFilled = true;
    setTimeout(() => (this.contactAutoFilled = false), 2500);
  }

  companyChoiceName(assoc: ContactCompanyResponse): string {
    return assoc.company?.commercialName || `Empresa #${assoc.idCompany}`;
  }

  // ===== Avatars helpers =====
  getContactInitials(contact: ContactRow): string {
    const f = (contact.fullname ?? '').trim().charAt(0);
    const l = (contact.lastname ?? '').trim().charAt(0);
    return (f + l).toUpperCase() || '?';
  }
  getContactAvatarColor(contact: ContactRow): string {
    return this.avatarColors[contact.id % this.avatarColors.length];
  }
  getCompanyInitials(company: { id: number; commercialName: string }): string {
    const parts = (company.commercialName ?? '').trim().split(/\s+/);
    return ((parts[0]?.charAt(0) ?? '') + (parts[1]?.charAt(0) ?? '')).toUpperCase() || '?';
  }
  getCompanyAvatarColor(company: { id: number; commercialName: string }): string {
    return this.avatarColors[company.id % this.avatarColors.length];
  }

  // ===== Validación helpers =====
  hasError(controlName: string): boolean {
    const ctrl = this.form.get(controlName);
    return !!(ctrl && ctrl.invalid && (ctrl.dirty || ctrl.touched));
  }

  getError(controlName: string): string {
    const ctrl = this.form.get(controlName);
    if (!ctrl || !ctrl.errors) return '';
    if (ctrl.errors['required']) return 'Este campo es obligatorio';
    if (ctrl.errors['maxlength']) {
      return `Máximo ${ctrl.errors['maxlength'].requiredLength} caracteres`;
    }
    return 'Valor inválido';
  }

  // ===== Save =====
  guardar(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.saving = true;
    this.error = null;
    const raw = this.form.getRawValue();

    if (this.isEditing && this.ticketId != null) {
      const patch: TicketUpdateRequest = this.buildUpdateRequest(raw);
      this.ticketsApi.updateTicket(this.ticketId, patch).subscribe({
        next: (ticket) => {
          this.saving = false;
          this.toast.updated('Ticket');
          this.saved.emit(ticket);
          if (!this.drawerMode) {
            this.router.navigate(['/tickets']);
          }
        },
        error: (err) => {
          this.saving = false;
          const msg = err.error?.message || 'Error al actualizar el ticket';
          this.error = msg;
          this.toast.error({ title: 'Error', message: msg });
        }
      });
    } else {
      this.submitCreate(false);
    }
  }

  /** Ejecuta el POST de creación. Si allowDuplicate=false y hay duplicados, muestra el aviso (INC 17). */
  private submitCreate(allowDuplicate: boolean): void {
    const raw = this.form.getRawValue();
    const req: TicketRequest = this.buildCreateRequest(raw);
    this.saving = true;
    this.error = null;
    this.ticketsApi.createTicket(req, allowDuplicate).subscribe({
      next: (ticket) => {
        this.saving = false;
        this.duplicateWarning = null;
        this.toast.created('Ticket');
        this.saved.emit(ticket);
        if (!this.drawerMode) {
          this.router.navigate(['/tickets']);
        }
      },
      error: (err) => {
        this.saving = false;
        // 409 con duplicados: mostrar aviso en vez de error genérico.
        if (err.status === 409 && Array.isArray(err.error?.duplicates)) {
          this.duplicateWarning = err.error.duplicates as Ticket[];
          return;
        }
        const msg = err.error?.message || 'Error al registrar el ticket';
        this.error = msg;
        this.toast.error({ title: 'Error', message: msg });
      }
    });
  }

  /** INC 17: el usuario decide crear igual pese a los duplicados. */
  crearDeTodosModos(): void {
    this.duplicateWarning = null;
    this.submitCreate(true);
  }

  /** INC 17: descartar el aviso de duplicados sin crear. */
  descartarDuplicados(): void {
    this.duplicateWarning = null;
  }

  /** INC 17: abrir un ticket duplicado existente. */
  abrirTicketExistente(t: Ticket): void {
    this.duplicateWarning = null;
    if (this.drawerMode) {
      this.cancelled.emit();
    }
    // INC 50: la vista de detalle es un modal sobre la lista.
    this.router.navigate(['/tickets'], { queryParams: { openTicket: t.id } });
  }

  private buildCreateRequest(raw: any): TicketRequest {
    const req: TicketRequest = {
      subject: raw.subject,
      source: (raw.source as TicketSource) || 'MANUAL'
    };
    if (raw.description) req.description = raw.description;
    if (raw.idTicketPriority != null) req.idTicketPriority = raw.idTicketPriority;
    // INC 41: no se envía idTicketStatus al crear — el backend asigna el status is_default.
    if (raw.idTicketCategory != null) req.idTicketCategory = raw.idTicketCategory;
    if (raw.idPipeline != null) req.idPipeline = raw.idPipeline;
    if (raw.idStage != null) req.idStage = raw.idStage;
    if (raw.intIdUserOwner != null) req.intIdUserOwner = raw.intIdUserOwner; 
    if (this.selectedContact?.id) req.idContact = this.selectedContact.id;
    if (this.selectedCompany?.id) req.idCompany = this.selectedCompany.id;
    if (this.prefilledDealId != null) req.idDeal = this.prefilledDealId;
    if (this.prefilledLeadId != null) req.idLead = this.prefilledLeadId;  // Inc 25
    return req;
  }

  private buildUpdateRequest(raw: any): TicketUpdateRequest {
    const patch: TicketUpdateRequest = {
      subject: raw.subject,
      description: raw.description ?? undefined,
      idTicketPriority: raw.idTicketPriority ?? undefined,
      // INC 41: el status no se edita desde este form (se sincroniza por etapa en backend).
      idTicketCategory: raw.idTicketCategory ?? undefined,
      idPipeline: raw.idPipeline ?? undefined,
      idStage: raw.idStage ?? undefined,
      intIdUserOwner: raw.intIdUserOwner ?? undefined,
      idContact: this.selectedContact?.id ?? null,
      idCompany: this.selectedCompany?.id ?? null
    };
    // Inc 25: re-enviar los vínculos de origen (el prefilled solo existe al crear;
    // en edición se usa el valor cargado del ticket) para que editar un ticket
    // creado desde un lead no borre el vínculo lead↔ticket.
    const dealId = this.prefilledDealId ?? this.loadedDealId;
    if (dealId != null) {
      patch.idDeal = dealId;
    }
    const leadId = this.prefilledLeadId ?? this.loadedLeadId;
    if (leadId != null) {
      patch.idLead = leadId;
    }
    return patch;
  }

  cancelar(): void {
    if (this.drawerMode) {
      this.cancelled.emit();
    } else {
      this.router.navigate(['/tickets']);
    }
  }

  //Helpers 
  originLabel(source: string): string {
    return getOrigenLabel(source);
  }

  priorityLabel(priority: TicketPriority): string {
    return resolveConfigLabel((priority as any).label, priority.name, PRIORIDAD_LABELS);
  }

  placeholderFor(field: string): string {
    return getPlaceholder(field);
  }

  // Incidente 8

  categoryIndentFor(depth: number): string {
    return categoryIndent(depth);
  }
}
